import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  filter,
  finalize,
  firstValueFrom,
  ignoreElements,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BackendResponse } from 'src/app/interfaces/response.interface';
import { handleError } from 'src/app/tools/tools';
import { environment } from 'src/environments/environment';
import { UserI, UserWithToken } from '../../../interfaces/user.interface';

const USER_COOKIE_KEY = 'x-token';

type State = 'LOADING' | 'PARTIAL' | 'LOADED';

@Injectable({
  providedIn: 'root',
})
export class AuthLoginService {
  //TODO CREATE USER INTERFACE
  private userState = new BehaviorSubject<{
    user: UserI | null;
    state: State;
  }>({ user: null, state: 'LOADED' });

  // private user = new BehaviorSubject<UserWithToken | null>(null);

  private currentSystem = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly cookie: CookieService
  ) { }

  private url = environment.url;

  login(user: string, pass: string, param: Params) {
    let url = this.url + 'users/auth/login';

    return this.http
      .post<UserWithToken>(url, {
        username: user,
        pass: pass,
      })
      .pipe(
        tap((userToken) =>
          this.saveTokenToCookie(userToken.token.access_token)
        ),

        tap((userToken) => this.pushNewUser(userToken, 'PARTIAL')),

        tap(() => this.loadUserDataInBackground().subscribe()),

        switchMap(() => timer(150)),

        tap(() => this.redirect(param)),

        ignoreElements()
      );
  }

  private loadingUserData$: Observable<UserI> | null = null;
  private loadUserDataInBackground(): Observable<UserI> {
    // Ya hay una petición en curso
    if (this.loadingUserData$) {
      return this.loadingUserData$;
    }

    this.userState.next({
      ...this.userState.value,
      state: 'LOADING',
    });

    this.loadingUserData$ = this.http
      .get<{ ok: boolean; user: UserI }>(`${this.url}users/me/data`)
      .pipe(
        map((data) => data.user),

        tap((user) => {
          this.userState.next({
            user,
            state: 'LOADED',
          });
        }),

        finalize(() => {
          this.loadingUserData$ = null;
        }),

        shareReplay(1)
      );

    return this.loadingUserData$;
  }

  private redirect(param: Params) {
    const userState = this.userState.getValue();

    if (!userState.user) {
      this.router.navigate(['login']);
      return;
    }

    if (!userState.user.isOtp) {
      if (param['redirect']) {
        const ruta = param['redirect'];
        const p = { ...param };
        delete p['redirect'];
        this.router.navigate([ruta], { queryParams: p });
      } else this.router.navigate(['system-select']);
    } else {
      this.router.navigate(['new-pass']);
    }
  }

  logout() {
    this.removeUserFromCookie();
    this.removeUserFromLocalStorage();
    this.clearCurrentSystem();
    this.userState.next({ user: null, state: 'LOADED' });
    this.router
      .navigate(['login'], { replaceUrl: true })
      .catch((err) => console.error(err));
  }

  private pushNewUser({ user }: UserWithToken, state: State) {
    this.userState.next({ user, state });
  }

  saveTokenToCookie(token: string) {
    this.cookie.set(USER_COOKIE_KEY, token, undefined, '/');
  }

  private removeUserFromCookie() {
    this.cookie.delete(USER_COOKIE_KEY, '/');
  }

  private removeUserFromLocalStorage() {
    localStorage.removeItem('user-info');
  }

  async returnUserInfoV1(
    getState: State
  ): Promise<{ user: UserI; state: State } | null> {
    let direction = `${this.url}users/me`;
    try {
      const userData = await firstValueFrom(
        this.userState.pipe(
          take(1),
          switchMap((data) => {
            if (!data) {
              return this.http.get<UserWithToken>(direction).pipe(
                tap((userToken) => this.pushNewUser(userToken, 'PARTIAL')),
                tap(() => this.loadUserDataInBackground().subscribe()),
                catchError((error) => {
                  console.error(error);
                  return EMPTY;
                })
              );
            } else return of(data);
          })
        )
      );

      if (!userData?.user) return null;

      return { user: userData.user, state: 'PARTIAL' };
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private async waitForUserState(
    requiredState: State
  ): Promise<{ user: UserI; state: State }> {
    const state = await firstValueFrom(
      this.userState.pipe(
        filter((data) => {
          if (!data.user) {
            return false;
          }

          if (requiredState === 'PARTIAL') {
            return data.state === 'PARTIAL' || data.state === 'LOADED';
          }

          return data.state === 'LOADED';
        }),
        take(1)
      )
    );

    return {
      user: state.user!,
      state: state.state,
    };
  }

  async returnUserInfo(
    getState: State
  ): Promise<{ user: UserI; state: State } | null> {
    try {
      const current = this.userState.value;

      // Ya tenemos lo que necesitamos
      if (
        current.user &&
        (getState === 'PARTIAL' || current.state === 'LOADED')
      ) {
        return {
          user: current.user,
          state: current.state,
        };
      }

      // Ya hay una carga en progreso.
      // Esperamos sin hacer polling.
      if (current.state === 'LOADING') {
        return await this.waitForUserState(getState);
      }

      // No hay usuario: obtenemos el usuario inicial
      const userToken = await firstValueFrom(
        this.http.get<UserWithToken>(`${this.url}users/me`)
      );

      this.pushNewUser(userToken, 'PARTIAL');

      // Si solo necesitan PARTIAL, terminamos
      if (getState === 'PARTIAL') {
        return {
          user: userToken.user,
          state: 'PARTIAL',
        };
      }

      // Si necesitan LOADED, esperamos
      this.loadUserDataInBackground().subscribe();

      return await this.waitForUserState('LOADED');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async returnUserInfoV2(
    getState: State
  ): Promise<{ user: UserI; state: State } | null> {
    try {
      const current = this.userState.value;

      // ─────────────────────────────────────
      // 1. Ya tenemos usuario en memoria
      // ─────────────────────────────────────
      if (current.user) {
        if (getState === 'PARTIAL' || current.state === 'LOADED') {
          return {
            user: current.user,
            state: current.state,
          };
        }

        // Está cargando → esperamos
        return await this.waitForUserState(getState);
      }

      // ─────────────────────────────────────
      // 2. No tenemos usuario en memoria
      //    (ej: F5 / reload)
      // ─────────────────────────────────────
      const userToken = await firstValueFrom(
        this.http.get<UserWithToken>(`${this.url}users/me`)
      );

      this.pushNewUser(userToken, 'PARTIAL');

      // ─────────────────────────────────────
      // 3. Solo necesitan PARTIAL
      // ─────────────────────────────────────
      if (getState === 'PARTIAL') {
        return {
          user: userToken.user,
          state: 'PARTIAL',
        };
      }

      // ─────────────────────────────────────
      // 4. Necesitan LOADED
      // ─────────────────────────────────────
      this.loadUserDataInBackground().subscribe();

      return await this.waitForUserState('LOADED');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  getUserInfoById(userId: number): Observable<UserI> {
    const direction = `${this.url}users/${userId}/info`;
    return this.http.get<UserI>(direction);
  }

  resetPassword(email: string) {
    const direction = `${this.url}users/reset-password`;
    return this.http.post(direction, { email: email }).pipe();
  }

  checkToken() {
    return this.http
      .post<{ ok: boolean; firstLogin: string; isOtp: boolean }>(this.url, {})
      .pipe(catchError((err) => throwError(() => err)));
  }

  changePassword(data: { id: string; oldPass: string; newPass: string }) {
    const direction = this.url + 'users/auth/change-password/withoutold';
    return this.http.post<BackendResponse>(direction, data).pipe(
      catchError((error) => handleError(error)),
      map((data) => data.result as UserI)
    );
  }

  updateGroup(group: any) {
    let direction = `${this.url}users/${group.id}`;
    return this.http.put<any>(direction, group).pipe(
      catchError((error) => handleError(error)),
      tap((data) => data)
    );
  }
  changePasswordWithOutOld(data: { id: string; newPass: string }) {
    const direction = this.url + 'users/auth/change-password';
    return this.http.post<BackendResponse>(direction, data).pipe(
      catchError((error) => handleError(error)),
      map((data) => data.result as UserI)
    );
  }

  setCurrentSystem(system: string) {
    this.currentSystem.next(system);
    sessionStorage.setItem('currentSystem', system);
  }

  // 🔹 Obtiene el sistema actual
  getCurrentSystem(): string | null {
    return this.currentSystem.value || sessionStorage.getItem('currentSystem');
  }

  // 🔹 Limpia el sistema actual (se puede llamar al hacer logout)
  clearCurrentSystem() {
    this.currentSystem.next(null);
    sessionStorage.removeItem('currentSystem');
  }
}
